"use client";

import { useState } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { filterChatIcons } from "@/models/chat/chatIcons";
import { twMerge } from "@/utils/twMerge";

import { useTreeItemControlTabIndex } from "../useSidebarNav";

import type { ChatModel } from "@/models/chat/ChatModel";

const ChatIconPickerPanelContent = observer(function ChatIconPickerPanelContent({
  chat,
  close,
}: {
  chat: ChatModel;
  close: () => void;
}) {
  const t = useTranslations();
  const [query, setQuery] = useState("");
  const icons = filterChatIcons(query);

  return (
    <>
      <div className="flex items-center gap-1.5 h-[30px] px-1 py-[3px]">
        <Icon name="Search" className="w-6 h-6 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && query) {
              event.stopPropagation();
              setQuery("");
            }
          }}
          placeholder={t("documentsSidebar.searchIcons")}
          aria-label={t("documentsSidebar.searchIcons")}
          className={twMerge(
            "flex-1 bg-transparent border-none outline-none focus:ring-0",
            "text-[16px] leading-[21px] tracking-[-0.02em] font-geist",
            "text-document-text placeholder:text-gray-400",
          )}
        />
        {query && (
          <button type="button" aria-label={t("search.clear")} onClick={() => setQuery("")}>
            <Icon name="Cross" className="w-6 h-6" />
          </button>
        )}
      </div>
      <div className="flex items-center h-3">
        <div className="w-full h-px bg-block-border" />
      </div>
      {icons.length === 0 ? (
        <div className="px-1 py-2 text-[14px] text-gray-500">
          {t("documentsSidebar.noIconsFound")}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(6,32px)] justify-between">
          {icons.map((icon) => {
            const selected = chat.icon === icon.name;
            return (
              <button
                key={icon.name}
                type="button"
                className="flex items-center justify-center w-8 h-[34px]"
                aria-label={icon.name}
                aria-pressed={selected}
                title={icon.name}
                onClick={() => {
                  chat.updateIcon(icon.name).catch((err) => {
                    console.error("Failed to update chat icon:", err);
                  });
                  close();
                }}
              >
                <span
                  className={twMerge(
                    "flex items-center justify-center w-8 h-8 rounded-8",
                    selected
                      ? "bg-blue-100 dark:bg-blue-900"
                      : "hover:bg-sidebar-button-hover-inverted-bg",
                  )}
                >
                  <Icon name={`chat-icons/${icon.name}`} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
});

export const ChatIconPicker = observer(function ChatIconPicker({ chat }: { chat: ChatModel }) {
  const t = useTranslations();
  const controlTabIndex = useTreeItemControlTabIndex();

  return (
    <Popover>
      <PopoverButton
        type="button"
        aria-label={t("documentsSidebar.chatIcon")}
        tabIndex={controlTabIndex}
        className={twMerge(
          "h-6 shrink-0 flex items-center justify-center rounded-4 outline-none",
          "hover:bg-blue-100 hover:dark:bg-blue-900",
        )}
      >
        <Icon name={`chat-icons/${chat.icon}`} />
      </PopoverButton>
      <PopoverPanel
        focus
        portal
        anchor={{ to: "bottom start", gap: 4, padding: 10 }}
        className={twMerge(
          "w-[268px] max-h-[min(60vh,420px)] overflow-y-auto no-scrollbar",
          "bg-block-bg shadow-md dark:shadow-block-inverted rounded-12 p-2 z-100 outline-none",
        )}
      >
        {({ close }) => <ChatIconPickerPanelContent chat={chat} close={close} />}
      </PopoverPanel>
    </Popover>
  );
});
